import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '@/store/hooks';
import { RootState } from '@/store/rootReducer';
import { LoadingStatus } from '@/enums/LoadingStatus.enum';
import {
  ClientDecision,
  setClientDecisions,
  setEntityStatus,
} from '@/store/slices/masterDataSlice';
import { masterDataIdbService } from '@/store/idbStorage/masterDataIdbService';
import type { MasterDataEntityKey } from '@/store/idbStorage/masterDataIdbService';
import { API_ENDPOINTS } from '@/api/endpoints.constant';
import { clivApiRequest } from '@/utils/apiClient';
import { systemConfig } from '@/config/systemConfig';
import { captureApiFailure } from '@/utils/sentryApiError.util';
import {
  MasterDataHookOptions,
  shouldUseMasterDataCache,
} from '@/hooks/masterData/masterDataHookOptions';

const ENTITY_KEY: MasterDataEntityKey = 'md_clientDecisions';

export const useClientDecisions = (options?: MasterDataHookOptions) => {
  const refreshIfEmpty = options?.refreshIfEmpty ?? false;
  const dispatch = useDispatch();
  const clientDecisions = useAppSelector((state: RootState) => state.masterData.clientDecisions);
  const status = useAppSelector(
    (state: RootState) => state.masterData.entityStatus['clientDecisions'] ?? LoadingStatus.IDLE
  );

  useEffect(() => {
    if (status !== LoadingStatus.IDLE) {
      return;
    }

    const load = async () => {
      dispatch(setEntityStatus({ entity: 'clientDecisions', status: LoadingStatus.LOADING }));
      try {
        const cached = await masterDataIdbService.getEntity<ClientDecision[]>(ENTITY_KEY);
        if (shouldUseMasterDataCache(cached, refreshIfEmpty)) {
          dispatch(setClientDecisions(cached));
          dispatch(setEntityStatus({ entity: 'clientDecisions', status: LoadingStatus.SUCCEEDED }));
          return;
        }
        const response = await clivApiRequest<ClientDecision[]>({
          url: `${systemConfig.apiUrl}/${API_ENDPOINTS.META.CLIENT_DECISIONS}`,
        });
        if (response.ok) {
          const data = response.data || [];
          dispatch(setClientDecisions(data));
          await masterDataIdbService.setEntity(ENTITY_KEY, data);
          dispatch(setEntityStatus({ entity: 'clientDecisions', status: LoadingStatus.SUCCEEDED }));
        } else {
          captureApiFailure('Error fetching client decisions', { status: response.status });
          dispatch(setEntityStatus({ entity: 'clientDecisions', status: LoadingStatus.FAILED }));
        }
      } catch (error) {
        captureApiFailure('Error fetching client decisions', { error });
        dispatch(setEntityStatus({ entity: 'clientDecisions', status: LoadingStatus.FAILED }));
      }
    };

    void load();
  }, [status, dispatch, refreshIfEmpty]);

  return { clientDecisions, status };
};
